import { PRODUCT_NAME } from '../constants/brand';

export const EN = {
  common: {
    appName: PRODUCT_NAME,
    back: 'Back',
    close: 'Close',
    closeDocument: 'Close document',
    done: 'Done',
    save: 'Save',
    cancel: 'Cancel',
    continue: 'Continue',
    apply: 'Apply',
    read: 'Open document',
    copy: 'Copy',
    share: 'Share',
    loading: 'Loading',
    sending: 'Sending...',
    preparing: 'Preparing...',
    requesting: 'Requesting...',
    retrying: 'Retrying...',
    tryAgain: 'Try again',
    openTelegram: 'Open Telegram',
    closeApp: 'Close app',
    later: 'Later',
    copied: 'Copied',
    valueCopied: '{{label}} copied',
    failedToCopy: 'Failed to copy',
    contactSupport: 'Contact support',
    backToHome: 'Back to home',
    linkCopied: 'Link copied',
    stepOf: 'Step {{current}} of {{total}}',
    copiedLabels: {
      amount: 'Amount',
      wallet: 'Wallet',
      cardNumber: 'Card number',
      expiry: 'Expiry',
      cvv: 'CVV',
      nameOnCard: 'Name on card',
      address: 'Address',
      postalCode: 'Postal code',
      city: 'City',
      issuingCountry: 'Issuing country',
    },
    second: 'second',
    seconds: 'seconds',
  },

  startup: {
    loaderTitle: 'Preparing your account',
    telegramRequiredTitle: 'Open in Telegram',
    telegramRequiredText: 'To use the service, open it through the Telegram Mini App.',
    referralRequiredTitle: 'Referral code required',
    referralRequiredText:
      'Our service is available by referral code only. Get an invite code and open the app again.',
    loginErrorTitle: 'Unable to sign in',
    loginErrorText: 'Check your connection and try again.',
    sessionExpiredTitle: 'Session expired',
    sessionExpiredText:
      'For your security, this session has ended. Please reopen the app in Telegram to continue.',
    pmRequiredTitle: 'Allow Telegram messages',
    pmDeniedTitle: 'Messages are required',
    pmText:
      'We need permission to send you private Telegram messages. Access is not available without it.',
    allowMessages: 'Allow messages',
    openTelegram: 'Open Telegram',
  },

  navigation: {
    main: 'Main navigation',
    home: 'Home',
    history: 'History',
    referral: 'Referral',
    profile: 'Profile',
    topUp: 'Top up',
  },

  onboarding: {
    heroTitleLine1: 'Spend crypto',
    heroTitleLine2: 'like cash',
    heroSubtitle: 'Top up with USDT and pay anywhere your debit card is accepted.',
    getStarted: 'Get started',
    feesLimitsTitle: 'Fees & limits',
    topUps: 'TOP UPS',
    card: 'CARD',
    usdtTopUp: 'USDT top-up',
    free: 'Free',
    minimumTopUp: 'Minimum top-up',
    virtualCardIssuance: 'Virtual card issuance',
    cardPayments: 'Card payments',
    noFee: 'No fee',
    sharePhoneTitle: 'Share your phone number',
    sharePhoneText:
      'We use the number from your Telegram account to secure your card and help recover access. We never share it with third parties.',
    sharePhoneAction: 'Share phone',
    sharePhoneNumberAction: 'Share phone number',
    sharingPhone: 'Sharing...',
    phoneNumberShared: 'Phone number shared',
    requestedSecurely: 'Requested securely via Telegram',
    telegramOpenRequired: 'Open the app in Telegram',
    phoneConfirmRequestFailed: 'Failed to request phone confirmation. Try again.',
    registrationFailed: 'Failed to finish registration. Try again.',
  },

  documents: {
    termsTitle: 'Terms & privacy',
    submitTitle: 'Submit your documents',
    submitText: 'Review and accept the required documents to continue opening your account.',
    updateTitle: 'Updated documents',
    updateText:
      `Review and accept the latest legal documents to continue using ${PRODUCT_NAME}.`,
    loadingDocuments: 'Loading documents',
    noDocuments: 'No documents are available.',
    agreeToDocument: 'I agree to the {{title}}',
    submitDocuments: 'Accept Terms',
    acceptUpdates: 'Accept Terms',
    submitting: 'Submitting',
    legalDocument: 'Legal document',
    version: 'Version {{version}}',
    validFrom: 'Valid from {{date}}',
    contentUnavailable: 'Document content is not available.',
    loadingDocument: 'Loading document',
    submitFailed: 'Failed to submit documents.',
    loadDocumentsFailed: 'Failed to load documents.',
    loadDocumentFailed: 'Failed to load document.',
  },

  pin: {
    createTitle: 'Create a passcode',
    createSubtitle: 'Choose a 4-digit code to open the app',
    confirmTitle: 'Confirm passcode',
    confirmSubtitle: 'Re-enter your 4-digit code',
    passcodeMismatch: "Passcodes don't match. Try again.",
    setFailed: 'Failed to set passcode. Try again.',
    setDoneTitle: 'Passcode set',
    setDoneText: 'You’ll enter this code each time you open the app.',
    unlockTitle: 'Enter passcode',
    unlockSubtitle: 'Enter your 4-digit code to unlock',
    forgotPin: 'Forgot PIN',
    forgotPinButton: 'Forgot your PIN?',
    recoverAccess: 'Recover access',
    chooseCodeChannel: 'Choose how you want to receive the verification code.',
    codeChannelLabel: 'Code channel',
    enterCode: 'Enter code',
    verificationCode: 'Verification code',
    incorrectPasscode: 'Incorrect passcode. Try again.',
    biometricAuthFailed: 'Biometric authentication failed.',
    biometricUnlockFailed: 'Failed to unlock with biometrics.',
    delete: 'Delete',
    useBiometrics: 'Use biometrics',
    currentTitle: 'Enter current passcode',
    currentSubtitle: 'Enter your current 4-digit code',
    newTitle: 'Create new passcode',
    newSubtitle: 'Choose a new 4-digit code',
    confirmNewTitle: 'Confirm new passcode',
    confirmNewSubtitle: 'Re-enter your new 4-digit code',
    changed: 'Passcode changed.',
    updateFailed: 'Failed to update passcode. Try again.',
    sendCode: 'Send code',
    resendCode: 'Resend code',
    resendIn: 'Resend in {{time}}',
    sendCodeIn: 'Send code in {{time}}',
    codeSentVia: 'We sent a 6-digit code via {{channel}} to {{target}}.',
    codeSent: 'We sent a 6-digit code to {{target}}.',
    invalidCode: 'Invalid code. Try again.',
    tooManyCodeAttempts: 'Too many attempts. Request a new code to continue.',
    availableIn: 'Available in {{time}}',
    requestAnotherCodeIn: 'You can request another code in {{time}}.',
    noRecoveryChannel: 'No recovery channel is available for this account.',
    codeFieldsAria: '{{label}} fields',
  },

  channels: {
    email: {
      label: 'Email',
      description: 'Send code by email',
    },
    telegram: {
      label: 'Telegram',
      description: 'Send code in Telegram',
    },
    sms: {
      label: 'SMS',
      description: 'Send code by text message',
    },
    whatsapp: {
      label: 'WhatsApp',
      description: 'Send code in WhatsApp',
    },
    call: {
      label: 'Call',
      description: 'Get code by phone call',
    },
  },

  home: {
    welcomeBack: 'Welcome back',
    notifications: 'Notifications',
    profile: 'Profile',
    recentActivity: 'Recent activity',
    seeAll: 'See all',
    addNewCard: 'Issue new card',
    cardIssueInProgress: 'Finish your current card request before issuing a new one.',
    cardLimitReached: 'The limit of 5 cards has been reached.',
    cards: 'Cards',
    loadingOperations: 'Loading operations for this card',
    noOperationsTitle: 'No operations on this card yet',
    noOperationsText: 'Pay with this card or top it up, and operations will appear here.',
  },

  card: {
    myCard: 'My card',
    newCard: 'New card',
    cardCreated: 'Card created',
    cardCreatedText: 'Your new virtual card is ready to use.',
    appearance: 'Card appearance',
    chooseStyle: 'Choose your style',
    styleAppliedText: 'Applies to your virtual card and preview.',
    edit: 'Edit',
    renameCard: 'Rename card',
    cardName: 'Card name',
    chooseCard: 'Choose a card',
    chooseCardForTopUp: 'Choose a card to top up.',
    freezeQuestion: 'Freeze card?',
    freezeAction: 'Freeze',
    freezeText:
      'Payments on {{cardNumber}} will be declined while the card is frozen. You can unfreeze it anytime.',
    unfreeze: 'Unfreeze',
    unfreezeCard: 'Unfreeze card',
    frozenNotice: 'Card is frozen. Payments and top-ups are declined until you unfreeze it.',
    frozen: 'Frozen',
    holded: 'Holded',
    processingFundsNotice:
      '{{amount}} is being processed and awaiting blockchain confirmation. Your balance will update automatically once confirmed.',
    heldFundsNotice:
      'You have {{heldAmount}} holded. To add these funds to your balance, top up at least {{minimumAmount}}.',
    blocked: 'Blocked',
    blockedTitle: 'Card blocked',
    blockedNotice: 'This card is disabled. Payments, top-ups and card actions are not available.',
    activating: 'Activating',
    activatingTitle: 'Card issuing',
    activatingIssue: 'Issuing',
    activatingNotice: 'Your card request is being processed. The card will become available automatically.',
    issueConfirmingTitle: 'Waiting for confirmations',
    issueConfirmingText:
      'After the network receives all required confirmations, your card will be sent for issuing. Wait for a message from the bot.',
    issueSubmittedTitle: 'Card request submitted',
    issueSubmittedText:
      'Your card has been sent for issuing. Wait for a message from the bot.',
    pending: 'Pending',
    pendingIssue: 'Pending issue',
    pendingCard: 'Pending card',
    completeActivation: 'Complete activation',
    topUpToActivate: 'Top up to activate',
    issueTopUpRequired: 'Top up {{amount}} to send this card for issuing.',
    issueCost: 'Card issuance cost',
    issueMaintenanceTitle: 'Maintenance in progress',
    issueMaintenanceText: 'Card issuance is temporarily unavailable. Please come back later.',
    issuePaymentReceivedTitle: 'Payment received',
    issuePaymentReceivedText:
      'We received the required top-up. After network confirmations are complete, the card will be sent for issuing. Wait for a message from the bot.',
    continueSetup: 'Continue setup',
    cancelRequest: 'Cancel request',
    cancelRequestQuestion: 'Cancel card request?',
    cancelRequestText: 'This will remove the pending card and stop the issue flow.',
    revealDetails: 'Reveal details',
    cardDetails: 'Card details',
    cardNumber: 'Card number',
    expiry: 'Expiry',
    pin: 'PIN',
    changePin: 'Change PIN',
    changePinDescription: '6-digit card PIN',
    newPinTitle: 'Create a new card PIN',
    newPinSubtitle: 'Enter 6 unique digits without sequences like 123 or 321',
    confirmPinTitle: 'Confirm card PIN',
    confirmPinSubtitle: 'Re-enter your new 6-digit card PIN',
    pinFormatError: 'Enter a 6-digit PIN.',
    pinRepeatedDigits: 'PIN digits cannot be repeated.',
    pinConsecutiveDigits: 'PIN cannot contain 3 consecutive digits.',
    pinMismatch: "PINs don't match. Try again.",
    pinChanged: 'Card PIN changed successfully.',
    pinUpdateFailed: 'Failed to change card PIN. Try again.',
    nameOnCard: 'Name on card',
    issuingCountry: 'Issuing country',
    city: 'City',
    address: 'Address',
    postalCode: 'Postal code',
    moreOptions: 'More options',
    closeDetails: 'Close details',
    hidesAutomaticallyIn: 'Hides automatically in {{time}}',
    addToApplePay: 'Add to Apple Pay',
    addToGooglePay: 'Add to Google Pay',
    virtual: 'Virtual',
    virtualDescription: 'For any payments',
    physical: 'Plastic',
    physicalDescription: 'Plastic card request',
    soon: 'Soon',
    cardNameSection: 'CARD NAME',
    cardNamePlaceholder: 'Main card',
    nameOnCardSection: 'NAME ON CARD',
    namePlaceholder: 'JOHN SMITH',
    styleSection: 'CARD STYLE',
    typeSection: 'CARD TYPE',
    main: 'Main',
    travel: 'Travel',
    savings: 'Savings',
    styles: {
      sunset: 'Sunset',
      aurora: 'Aurora',
      mint: 'Mint',
      lagoon: 'Lagoon',
    },
  },

  walletGuide: {
    addAppleTitle: 'Add card to Apple Pay',
    addAppleSubtitle:
      'Follow these steps in Wallet, then confirm the card with the SMS code sent to Telegram.',
    addGoogleTitle: 'Add card to Google Pay',
    addGoogleSubtitle:
      'Follow these steps in Google Pay, then confirm the card with the SMS code sent to Telegram.',
    startWallet: 'Start in Wallet',
    startGoogle: 'Start in Google Pay',
    enterDetails: 'Enter card details',
    confirmWithCode: 'Confirm with code',
    walletScreen: 'Wallet',
    googleScreen: 'Google Pay',
    cardDetailsScreen: 'Card Details',
    paymentCardScreen: 'Payment card',
    verificationScreen: 'Verification',
    verifyCardScreen: 'Verify card',
    appleStartBullets: [
      'Open the Wallet app.',
      'Tap the plus button in the top right corner.',
      'Choose Debit or Credit Card.',
      'Tap Continue.',
    ],
    appleDetailsBullets: [
      'Tap Enter Card Details Manually.',
      `Copy the card number, expiry date and CVV from ${PRODUCT_NAME}.`,
      'Tap Next, then enter expiry date and CVV.',
    ],
    appleCodeBullets: [
      'Choose SMS from the verification methods.',
      'The code will arrive in Telegram.',
      'Paste the code into Verification Code and tap Next.',
    ],
    googleStartBullets: [
      'Open Google Pay or Google Wallet.',
      'Tap Add to Wallet or the add card button.',
      'Choose Payment card.',
      'Tap New credit or debit card.',
    ],
    googleDetailsBullets: [
      `Enter the card number, expiry date and CVV from ${PRODUCT_NAME}.`,
      'Tap Continue.',
      'Choose SMS as the verification method.',
    ],
    googleCodeBullets: [
      'Copy the verification code from Telegram.',
      'Paste it into the Code field.',
      'Tap Continue to finish setup.',
    ],
  },

  deposit: {
    topUp: 'Top up',
    topUpCard: 'Top up card',
    topUpAddress: 'Top up address',
    noTopUpCardsTitle: 'No active card yet',
    noTopUpCardsText: 'Your card is being issued. You can top it up once it becomes active.',
    notIssuedTopUpText: 'Complete the required top-up to send this card for issuing.',
    currencyNetwork: 'Currency and network',
    selectCurrency: 'Select currency',
    selectNetwork: 'Select network',
    selectCurrencyNetwork: 'Select currency and network',
    topUpAmount: 'Top-up amount',
    cardBalanceReceive: 'Card balance will receive',
    amount: 'Amount',
    amountWithAsset: 'Amount {{asset}}',
    processingFee: 'Processing fee',
    walletAddress: 'Wallet address {{asset}}',
    received: 'Received',
    transactions: 'Transactions',
    noTransactionsYet: 'New transactions will appear here after we detect them.',
    confirmationsValue: '{{current}}/{{total}} confirmations',
    confirmed: 'Confirmed',
    networkConfirmations: 'Network confirmations',
    confirming: 'Confirming',
    confirmingOnChain: 'Confirming on-chain',
    network: 'Network',
    status: 'Status',
    topUpFailed: 'Top up failed',
    topUpFailedText: 'The funds were not credited to your balance.',
    topUpCompleted: 'Top up completed',
    topUpReceived: 'Top up received',
    addedToBalance: '{{amount}} has been added to your balance.',
    viewOnExplorer: 'View on {{explorer}}',
    viewOnBlockExplorer: 'View on block explorer',
    depositAmount: 'Deposit amount',
    transferDetected:
      'We detected your transfer. The card balance will update automatically after the network confirms it.',
    issueTransferDetected:
      'We detected your transfer. Card issuing will continue automatically after the network confirms it.',
    qrLabel: 'Top-up wallet QR code',
    showAddress: 'Show top-up address',
    selectFirst: 'Select currency and network first.',
    minimumAmount: 'Minimum top-up is {{amount}} {{asset}}.',
    warning:
      'Send only the selected asset and network. Smaller deposits could be ignored.',
    doNotSendLess: 'Do not send less than the minimum amount.',
    doNotSendLessAmount: 'Do not send less than {{amount}} {{asset}}.',
    autoCreditText: 'We will credit your card automatically after the transfer is detected.',
    autoIssueText:
      'We will issue your card automatically after the received amount reaches the required top-up amount.',
    viaNetwork: 'Top up via {{network}}',
    depositAsset: 'Deposit {{asset}}',
    doneWaiting: 'Done waiting',
  },

  history: {
    title: 'History',
    searchPlaceholder: 'Search transactions',
    exportAria: 'Export history',
    filterAria: 'Filter and sort',
    transactionTypeAria: 'Transaction type',
    clearSearchAria: 'Clear search',
    emptyTitle: 'No transactions found',
    noOperationsTitle: 'No card operations yet',
    noOperationsText: 'Make a card payment or top up your balance, and your operations will appear here.',
    noIncomeOperationsTitle: 'No top-ups yet',
    noIncomeOperationsText: 'Top up a card or receive a refund, and incoming operations will appear here.',
    noExpenseOperationsTitle: 'No spending yet',
    noExpenseOperationsText: 'Pay with your card, and card spending will appear in this section.',
    filteredEmptyTitle: 'No operations match these filters',
    filteredEmptyText: 'Try changing the period, cards, or operation type.',
    loading: 'Loading transactions',
    all: 'All',
    income: 'Income',
    expense: 'Expense',
    spending: 'Spending',
    totalsAria: 'Spending and income for the selected period',
    showSpendingAria: 'Show spending only',
    showIncomeAria: 'Show income only',
    showAllTransactionsAria: 'Show all transactions',
    loadingTotalsAria: 'Loading totals',
    today: 'Today',
    yesterday: 'Yesterday',
    thisWeek: 'This week',
    thisMonth: 'This month',
    allTime: 'All time',
    last90Days: 'Last 90 days',
    customRange: 'Custom range',
    allCards: 'All cards',
    allCardsCaption: 'Transactions across all your cards',
    periodFilter: 'Period',
    cardsFilter: 'Cards',
    filtersAria: 'History filters',
    openPeriodFilterAria: 'Choose period',
    openCardsFilterAria: 'Choose cards',
    clearPeriodFilterAria: 'Clear selected period',
    clearCardsFilterAria: 'Clear selected cards',
    selectPeriod: 'Select period',
    periodStart: 'Start',
    periodEnd: 'End',
    chooseDate: 'Choose a date',
    chooseEndDate: 'Choose an end date',
    wholeMonthAria: 'Select month: {{month}}',
    resetPeriod: 'Clear period',
    selectCardsTitle: 'Which cards should we show?',
    selectCardsSubtitle: 'Choose one or more cards for your transaction history.',
    cardsCount: 'Cards {{count}}',
    showTransactions: 'Show transactions',
    unnamedCard: 'Card',
    noCardsForFilterTitle: 'No cards available yet',
    noCardsForFilterText: 'Once a card is issued, you can select it here.',
    weekdaysShort: ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'],
    completed: 'Completed',
    pending: 'Pending',
    failed: 'Failed',
    newest: 'Newest',
    oldest: 'Oldest',
    highestAmount: 'Highest amount',
    dateRange: 'DATE RANGE',
    card: 'CARD',
    status: 'STATUS',
    sortBy: 'SORT BY',
    filterTitle: 'Filter and sort',
    exportTitle: 'Export history',
    exportDateRange: 'Date range',
    exportCsv: 'Export CSV',
    exportPreparing: 'Preparing...',
    exportFailed: 'Failed to export history.',
    exportNote:
      'Your full transaction history will be exported as a .csv file you can open in Excel, Sheets or send in chat.',
    downloadMissing: 'Download link is missing',
    downloadStarted: 'Download started',
    downloadOpened: 'Download link opened',
    downloadFailed: 'Failed to start download',
    downloadFile: 'Download file',
    exportReady: 'Export ready',
    exportReadyText: 'Your transaction history has been exported.',
    transaction: 'Transaction',
    transactionNotFound: 'Transaction not found.',
    merchantNameInfo: {
      title: 'Don’t recognize the name?',
      explanation: 'Merchant names are identified automatically and may be incorrect.',
      originalDescription: 'Original description',
      unavailable: 'Not available for this transaction',
      compare: 'Check the date and amount against your receipt.',
      unrecognized: 'Still don’t recognize the payment? Freeze your card and contact support.',
    },
    tryDifferentSearch: 'Try a different merchant, date or amount.',
    resultsSummary: '{{count}} RESULTS · {{total}}',
    resetAll: 'Reset all',
    loadMore: 'Load more',
    txHash: 'Tx hash',
    exchange: 'Exchange',
    sent: 'Sent',
    reference: 'Reference',
    category: 'Category',
    date: 'Date',
    paidAmount: 'Paid',
    amount: 'Amount',
    cardLabel: 'Card',
    shopping: 'Shopping',
    foodDrink: 'Food & drink',
    entertainment: 'Entertainment',
    topUp: 'Top up',
    topUpUsdt: 'Top up USDT',
    depositAccount: 'Account top-up',
    cardIssuancePayment: 'Card issuance payment',
    cashWithdrawal: 'Cash withdrawal',
    paymentCanceled: 'Payment canceled',
    canceledStatus: 'Canceled',
    refund: 'Refund',
    completedStatus: 'Completed',
    operationCategories: {
      shopping: 'Shopping',
      foodDrink: 'Food & drink',
      entertainment: 'Entertainment',
      topUp: 'Top up',
      cardIssuance: 'Card issuance',
      transport: 'Transport',
      travel: 'Travel',
      digitalServices: 'Digital services',
      financial: 'Financial services',
      health: 'Health',
      pets: 'Pets and veterinary care',
      agricultureGarden: 'Garden and agriculture',
      homeImprovement: 'Home improvement',
      printing: 'Printing',
      airlines: 'Airlines',
      carRental: 'Car rental',
      lodging: 'Hotels and lodging',
      rail: 'Rail transport',
      publicTransport: 'Public transport',
      ambulance: 'Ambulance',
      taxi: 'Taxi',
      deliveryStorage: 'Delivery and storage',
      cruises: 'Cruises',
      marine: 'Boats and marinas',
      travelServices: 'Travel services',
      tollsParking: 'Tolls and parking',
      otherTransport: 'Other transport',
      telecom: 'Telecom',
      internetServices: 'Internet services',
      moneyTransfer: 'Money transfers',
      televisionStreaming: 'TV and streaming',
      utilities: 'Utilities',
      businessSupplies: 'Business supplies',
      officeSupplies: 'Office supplies',
      marketplaces: 'Marketplaces',
      generalRetail: 'General retail',
      groceries: 'Groceries',
      vehicles: 'Vehicles',
      autoParts: 'Auto parts',
      fuel: 'Fuel',
      evCharging: 'EV charging',
      clothing: 'Clothing and shoes',
      homeFurniture: 'Furniture and home',
      weapons: 'Weapons and ammunition',
      electronics: 'Electronics',
      music: 'Music',
      restaurants: 'Restaurants',
      bars: 'Bars and nightlife',
      fastFood: 'Fast food',
      digitalMedia: 'Digital media',
      videoGames: 'Games',
      appsSoftware: 'Apps and software',
      pharmacies: 'Pharmacies',
      alcohol: 'Alcohol',
      secondHand: 'Second-hand and antiques',
      sportsOutdoors: 'Sports and outdoors',
      booksStationery: 'Books and stationery',
      jewelry: 'Jewelry and watches',
      hobbiesToys: 'Hobbies and toys',
      photo: 'Photography',
      giftsFlowers: 'Gifts and flowers',
      luggageLeather: 'Bags and leather goods',
      craftsArt: 'Arts and crafts',
      medicalSupplies: 'Medical supplies',
      beauty: 'Beauty and spa',
      insurance: 'Insurance',
      directMarketing: 'Direct sales',
      adult: 'Adult content',
      subscriptions: 'Subscriptions',
      religious: 'Religious organizations and goods',
      tobacco: 'Tobacco',
      specialtyRetail: 'Specialty retail',
      cashWithdrawal: 'Cash withdrawal',
      financialServices: 'Financial services',
      realEstate: 'Real estate',
      laundryCleaning: 'Laundry and cleaning',
      personalServices: 'Personal services',
      funeral: 'Funeral services',
      dating: 'Dating services',
      counseling: 'Counseling',
      businessServices: 'Business services',
      itServices: 'IT services',
      autoService: 'Auto service',
      repairs: 'Repairs',
      gambling: 'Gambling',
      cinema: 'Cinema and video',
      liveEntertainment: 'Events and entertainment',
      recreation: 'Sports and recreation',
      attractions: 'Attractions',
      healthcare: 'Doctors and clinics',
      dental: 'Dentistry',
      vision: 'Vision and optics',
      hospitalsCare: 'Hospitals and care',
      medicalLabs: 'Medical laboratories',
      legal: 'Legal services',
      education: 'Education',
      childcare: 'Childcare',
      charity: 'Charity',
      associations: 'Membership organizations',
      political: 'Political organizations',
      architectureEngineering: 'Architecture and engineering',
      accounting: 'Accounting and taxes',
      government: 'Government payments',
      taxes: 'Tax payments',
      postal: 'Postal services',
      emergency: 'Emergency services',
      intraCompany: 'Internal company payments',
      general: 'General',
    },
  },

  notifications: {
    title: 'Notifications',
    markAllRead: 'Mark all read',
    loadMore: 'Load more',
    loading: 'Loading notifications',
    loadingMore: 'Loading more notifications',
    emptyTitle: 'No notifications',
    emptyText: 'You are all caught up.',
    loadFailed: 'Could not load notifications.',
    loadMoreFailed: 'Could not load more notifications.',
    markReadFailed: 'Could not mark notifications as read.',
    today: 'TODAY',
    earlier: 'EARLIER',
    now: 'now',
    yesterday: 'Yesterday',
    locationLabel: 'Location',
    ipAddressLabel: 'IP address',
    deviceLabel: 'Device',
    fallbackTitle: 'Notification',
    fallbackDescription: 'Open the notification to review details.',
    passwordChangedTitle: 'Password changed',
    passwordChangedText: 'Your account password was successfully updated.',
    emailChangedTitle: 'Email changed',
    emailChangedText: 'Your account email was successfully updated.',
    phoneChangedTitle: 'Phone changed',
    phoneChangedText: 'Your account phone was successfully updated.',
    newLoginTitle: 'New account login',
    newLoginText: 'A new sign-in to your account was completed.',
    orderStatusTitle: 'Order status updated',
    orderStatusText: 'Review the order details.',
    exchangeStatusTitle: 'Exchange moved to a new status',
    exchangeStatusText: 'Review the exchange details.',
    offlineCheckFailedTitle: 'Offline check failed',
    offlineCheckFailedText: 'Review the data and the next verification step.',
    accountApprovedTitle: 'Account approved',
    accountApprovedText: 'Your account status changed to Approved.',
    accountRejectedTitle: 'Account rejected',
    accountRejectedText: 'Your account status changed to Rejected.',
    kycRequestedTitle: 'KYC form requested',
    kycRequestedText: 'A KYC form was requested for your account.',
    otpActivatedTitle: '2FA enabled',
    otpActivatedText: 'Two-factor authentication was enabled for your account.',
    otpDisabledTitle: '2FA disabled',
    otpDisabledText: 'Two-factor authentication was disabled for your account.',
    recoveryKeyUpdatedTitle: 'Recovery key updated',
    recoveryKeyUpdatedText: 'The 2FA recovery key was updated.',
  },

  referral: {
    eyebrow: 'Referral',
    title: 'Invite friends',
    heroTitle: 'Bring people in',
    heroText: 'Share your personal invite link with people you trust.',
    heroNote: "Earning from invited friends' spending is coming soon.",
    friendsJoined: 'Friends joined',
    copyLink: 'Copy link',
    copying: 'Copying...',
    sharePreparing: 'Preparing...',
    inviteShared: 'Invite message shared',
    shareFailed: 'Failed to share in Telegram',
    missingMessageId: 'Referral message id is missing',
    nativeShareUnavailable: 'Telegram native share is unavailable',
  },

  profile: {
    title: 'Profile',
    userFallback: 'User',
    personalInformation: 'Personal information',
    security: 'Security',
    notifications: 'Notifications',
    language: 'Language',
    helpSupport: 'Help & support',
    accountSection: 'Account',
    preferencesSection: 'App settings',
    currentLanguage: 'Current language',
    languageUpdateFailed: 'Failed to update language.',
    personal: {
      fullName: 'Full name',
      email: 'Email',
      phone: 'Phone',
      dateOfBirth: 'Date of birth',
      notAdded: 'Not added',
      notAvailable: 'Not available',
      telegram: 'Telegram',
      telegramId: 'Telegram ID',
      personalDetails: 'Personal details',
      connectedAccount: 'Connected account',
      editFullName: 'Edit full name',
      editEmail: 'Change email',
      editPhone: 'Change phone',
      editDob: 'Edit date of birth',
      updated: 'Profile updated',
      updateFailed: 'Failed to update personal information.',
      emailUpdated: 'Email updated',
      phoneUpdated: 'Phone updated',
      changeMethod: 'Change method',
      sendCode: 'Send code',
      codeSent: 'Code sent',
      invalidEmail: 'Enter a valid email address',
      invalidPhone: 'Enter a valid phone number',
    },
    securityPage: {
      accessSection: 'App access',
      appPasscode: 'App passcode',
      enabled: 'Enabled',
      notSet: 'Not set',
      changePasscode: 'Change passcode',
      setPasscode: 'Set passcode',
      biometric: 'Biometric sign-in',
      finishSetup: 'Finish setup',
      openTelegramSettings: 'Open Telegram settings',
    },
    biometric: {
      labels: {
        face: 'Face ID',
        finger: 'Touch ID',
        unknown: 'biometrics',
      },
      enabledTitle: 'Biometric sign-in enabled',
      enabledText: '{{productName}} can currently be unlocked with {{label}} on this device.',
      enabledSuccessText: 'You can now unlock {{productName}} with {{label}}.',
      disabledTitle: 'Biometric sign-in disabled',
      disabledText: 'Biometric sign-in has been turned off on this device.',
      notEnabledTitle: 'Biometrics not enabled',
      notEnabledText: 'You can enable Face ID or Touch ID later from this security screen.',
      finishTitle: 'Finish biometric setup',
      finishIntroText: 'Finish setup to unlock the app faster on this device.',
      checkPermissionTitle: 'Check permission',
      checkPermissionText: 'Return from Telegram settings, then check whether access is enabled.',
      couldNotEnableTitle: 'Could not enable biometrics',
      couldNotEnableText: 'Try again or set it up later from this security screen.',
      enableTitle: 'Enable {{label}}',
      enableText: 'Use {{label}} to unlock {{productName}} without entering your passcode.',
      accessReason: 'For fast, secure access to your account. Your biometric data stays on this device.',
      enableGeneric: 'Enable biometrics',
      enableSpecific: 'Enable {{label}}',
      finishText: '{{label}} permission is already allowed. Complete setup to use biometric sign-in on this device.',
      disabledAgainText: 'You can enable {{label}} again from this security screen.',
      checkPermissionBusyText:
        'After allowing {{label}} in Telegram settings, check the permission here to finish setup.',
      permissionNeededTitle: '{{label}} permission needed',
      permissionNeededText:
        '{{label}} permission is disabled in Telegram settings. Allow it again, then return here to finish setup.',
      openSettingsText:
        'Open Telegram biometric settings, allow {{productName}} to use {{label}}, then return here to finish setup.',
      openSettingsAction: 'Open {{label}} settings',
      turnOff: 'Turn off',
      turningOff: 'Turning off...',
      setup: 'Set up',
      settingUp: 'Setting up...',
      finishSetup: 'Finish setup',
      checkPermission: 'Check permission',
    },
    help: {
      intro:
        `Answers to the most common questions about ${PRODUCT_NAME} cards, top-ups, security, and account recovery.`,
      faq: 'Questions and answers',
      searchPlaceholder: 'Find an answer',
      showAll: 'Show all questions',
      noResults: 'No answers match your search.',
      supportTitle: 'Need help?',
      supportText: 'Message us and we will help you sort it out.',
      contact: 'Contact support on Telegram',
      contactOpening: 'Opening support chat...',
      contactFailed: 'Could not open the support chat. Please try again.',
      faqs: [
        {
          id: 'telegram-mini-app',
          question: `Why do I need to open ${PRODUCT_NAME} in Telegram?`,
          answer:
            `${PRODUCT_NAME} uses Telegram Mini App features for secure sign-in, notifications, contact sharing, and device-level flows. Opening it outside Telegram is not supported.`,
        },
        {
          id: 'pm-access',
          question: `Why does ${PRODUCT_NAME} ask to allow Telegram messages?`,
          answer:
            'We use private Telegram messages to send important service updates, one-time codes, top-up confirmations, and card security alerts. Access to the app requires this permission.',
        },
        {
          id: 'referral-only',
          question: 'Why do I need an invite link?',
          answer:
            `${PRODUCT_NAME} is currently available by referral only. You need a personal invite link from an existing user to create an account.`,
        },
        {
          id: 'create-account',
          question: 'How do I create an account?',
          answer:
            `Open ${PRODUCT_NAME} through an invite link, allow Telegram messages, share your phone number, accept the legal documents, and create your app passcode.`,
        },
        {
          id: 'phone-verification',
          question: 'Why do I need to verify my phone number?',
          answer:
            'Phone verification protects your account and is used for recovery, card security checks, and some payment confirmations.',
        },
        {
          id: 'passcode',
          question: 'What is the app passcode for?',
          answer:
            `Your passcode protects access to ${PRODUCT_NAME} inside Telegram. You will need it to unlock the app after registration.`,
        },
        {
          id: 'forgot-pin',
          question: 'What if I forgot my PIN?',
          answer:
            'Use Forgot your PIN on the unlock screen. You can receive a verification code by Telegram or email if email is available.',
        },
        {
          id: 'biometric',
          question: 'Can I use Face ID or Touch ID?',
          answer:
            'Yes, if Telegram reports biometrics as available on your device. You can enable or disable biometric sign-in from Security settings.',
        },
        {
          id: 'new-card',
          question: 'When do I get my card?',
          answer:
            `After phone verification, legal documents, and passcode setup are complete, ${PRODUCT_NAME} takes you to the new card flow.`,
        },
        {
          id: 'card-details',
          question: 'Where can I see my card details?',
          answer:
            'Open My card and reveal the details. Card number, expiry, CVV, and name on card can be copied from the details block.',
        },
        {
          id: 'freeze-card',
          question: 'What happens when I freeze a card?',
          answer:
            'A frozen card cannot be used for payments or top-ups. You can unfreeze it from the card details page.',
        },
        {
          id: 'top-up',
          question: 'How do USDT TRC-20 top-ups work?',
          answer:
            'Choose Top up, select an active card, and send USDT on TRC-20 to the provided address. Frozen cards are not available for top-ups.',
        },
        {
          id: 'top-up-pending',
          question: 'Why is my top-up still pending?',
          answer:
            `Top-ups need blockchain confirmations and internal processing. Make sure the transfer was sent on TRC-20 and to the exact address shown in ${PRODUCT_NAME}.`,
        },
        {
          id: 'wrong-network',
          question: 'What if I sent funds on the wrong network?',
          answer:
            'Do not send another transaction to fix it. Contact support with the transaction hash so the team can check whether recovery is possible.',
        },
        {
          id: 'tx-hash',
          question: 'Where can I check a top-up transaction?',
          answer:
            'Open the transaction details. For top-ups, the transaction hash links to the external block explorer when the backend provides the URL.',
        },
        {
          id: 'declined-payment',
          question: 'Why was my card payment declined?',
          answer:
            'A payment can be declined if the card is frozen, the balance is too low, the merchant is restricted, or the payment provider rejects the transaction.',
        },
        {
          id: 'apple-google-pay',
          question: 'Can I add the card to Apple Pay or Google Pay?',
          answer:
            'Yes. Open My card, reveal the details, then use the Add to Apple Pay or Google Pay guide to add the card manually.',
        },
        {
          id: 'notifications',
          question: 'What notifications will I receive?',
          answer:
            `${PRODUCT_NAME} sends security, login, card, and transaction notifications. The notification counter is shown on the home screen and in your profile.`,
        },
        {
          id: 'profile-data',
          question: 'How do I change my email, phone, or personal details?',
          answer:
            'Open Profile, then Personal information. Email and phone changes require a verification code before the new value is applied.',
        },
        {
          id: 'referrals',
          question: 'How do referrals work?',
          answer:
            'Open Referral to share your personal invite link. Friends who join through it will be connected to your invite.',
        },
        {
          id: 'history-export',
          question: 'Can I export transaction history?',
          answer:
            `Yes. Use export from History. When the export is ready, ${PRODUCT_NAME} asks Telegram to download the file from the backend link.`,
        },
        {
          id: 'session-expired',
          question: 'What should I do if my session expired?',
          answer:
            'Close and reopen the Telegram Mini App. This refreshes the Telegram init data and starts a new secure session.',
        },
        {
          id: 'support',
          question: 'How do I contact support?',
          answer:
            'Use Contact support on Telegram and include the affected card, transaction, top-up hash, or screenshot so the team can investigate faster.',
        },
      ],
    },
  },

  errors: {
    generic: 'Something went wrong. Try again.',
    failedSendCode: 'Failed to send code. Try again.',
    failedLoadNotifications: 'Could not load notifications.',
  },
};
